import {
  Box,
  Button,
  Card,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@dtx-company/design-system/src'
import { FormControlLabel, Radio } from '@mui/material'
import { PixelFormFieldNames, PixelProps } from './pixel.types'
import { PixelType } from '../../../containers/settings/settings.types'
import { usePixelState } from './pixel.hooks'
import CheckIcon from '@mui/icons-material/Check'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import styled from 'styled-components'

const StyledForm = styled.form`
  width: 100%;
`

export const Pixel = ({
  id,
  data,
  token,
  pageId,
  mutate,
  handleRemove
}: PixelProps): JSX.Element => {
  const {
    labelText,
    handleToggle,
    isEnabled,
    handleSubmit,
    onSubmit,
    errors,
    register,
    setDetailsOpen,
    detailsOpen,
    values,
    setValue,
    isSaveButtonDisabled,
    isSubmitting,
    typedData
  } = usePixelState({ id, data, token, pageId, mutate, handleRemove })
  return (
    <Stack gap={2}>
      <Stack gap={2}>
        <Typography id="share-flowpage">{labelText}</Typography>
        <Stack sx={{ pl: 4 }}>
          <Switch label={isEnabled ? 'Yes' : 'No'} checked={isEnabled} onChange={handleToggle} />
        </Stack>
      </Stack>
      {isEnabled && (
        <Card sx={{ p: 4 }}>
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <TextField
                type="text"
                label="Pixel Name"
                placeholder="Enter Name"
                inputProps={{ ...register(PixelFormFieldNames.NAME) }}
                error={!!errors[PixelFormFieldNames.NAME]?.message}
                helperText={errors[PixelFormFieldNames.NAME]?.message}
                labelAbove
              />
              <Box>
                <Button
                  variant="text"
                  type="button"
                  onClick={() => setDetailsOpen(open => !open)}
                  endIcon={detailsOpen ? ExpandLessIcon : ExpandMoreIcon}
                >
                  {detailsOpen ? 'Collapse ' : 'Expand '} Information
                </Button>
              </Box>
              {detailsOpen && (
                <Stack gap={1}>
                  <Stack aria-labelledby="pixel-type-label">
                    <RadioGroup
                      label="Pixel Type"
                      data-testid="pixel-type-label"
                      value={PixelType.iframe}
                      onChange={e => {
                        setValue(PixelFormFieldNames.PixelType, e.target.value as PixelType)
                      }}
                      formControlProps={{
                        sx: {
                          width: '100%'
                        }
                      }}
                    >
                      <FormControlLabel
                        value={PixelFormFieldNames.PixelType}
                        control={
                          <Radio
                            inputProps={{
                              ...register(PixelFormFieldNames.PixelType),
                              tabIndex: 0
                            }}
                            checked={values[PixelFormFieldNames.PixelType] === 'iframe'}
                            name="pixelType"
                            onChange={e => {
                              setValue(PixelFormFieldNames.PixelType, e.target.value as PixelType)
                            }}
                            value={PixelType.iframe}
                          />
                        }
                        label={'iFrame'}
                        sx={{
                          width: '100%',
                          marginRight: 0,
                          '.MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                      />
                      <FormControlLabel
                        value={PixelType.image}
                        control={
                          <Radio
                            inputProps={{
                              ...register(PixelFormFieldNames.PixelType)
                            }}
                            checked={values[PixelFormFieldNames.PixelType] === 'image'}
                            name="pixelType"
                            onChange={e => {
                              setValue(PixelFormFieldNames.PixelType, e.target.value as PixelType)
                            }}
                            value={PixelType.image}
                          />
                        }
                        label={'Image'}
                        sx={{
                          width: '100%',
                          marginRight: 0,
                          '.MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                      />
                    </RadioGroup>
                  </Stack>
                  <Stack>
                    <Stack direction={'row'} gap={3} alignItems={'center'}>
                      <Typography variant={'labelSmall'} color={'onSurface.secondary'}>
                        Pixel tracking URL
                      </Typography>
                      <Tooltip
                        placement={'top'}
                        arrow
                        title="In order to set up your pixel, all we need is the URL contained in the src attribute."
                      >
                        <Typography variant={'labelMedium'} color={'onSurface.secondary'}>
                          <HelpOutlineIcon fontSize={'inherit'} />
                        </Typography>
                      </Tooltip>
                    </Stack>
                    <TextField
                      label="Pixel tracking URL"
                      hiddenLabel
                      placeholder="Paste here"
                      inputProps={{ ...register(PixelFormFieldNames.SRC) }}
                      error={!!errors[PixelFormFieldNames.SRC]?.message}
                      helperText={errors[PixelFormFieldNames.SRC]?.message}
                    />
                  </Stack>
                </Stack>
              )}
              <Stack gap={4} direction={'row'}>
                <Button
                  type="submit"
                  fullWidth
                  disabled={isSaveButtonDisabled}
                  startIcon={CheckIcon}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  disabled={isSubmitting}
                  variant={'outlined'}
                  color={'error'}
                  onClick={() => handleRemove(id, typedData.isNotSaved)}
                  startIcon={DeleteOutlineIcon}
                  fullWidth
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </StyledForm>
        </Card>
      )}
    </Stack>
  )
}
